import getBodyTypeForResponse from './getBodyTypeForResponse';
import getRespondSubject from './getRespondSubject';
import getToCcRecipients from './getToCcRecipients';
import { ComposeOperation } from 'owa-mail-compose-store';
import buildReplyForwardMessageBody from '../utils/buildReplyForwardMessageBody';
import createItemIdForRequest from '../utils/createItemIdForRequest';
import getDocumentId from '../utils/getDocumentId';
import { ObservableMap } from 'mobx';
import { ARCHIVE_FOLDERS_TREE_TYPE, SHARED_FOLDERS_TREE_TYPE } from 'owa-folders';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { loadAllRecipientsForItem } from 'owa-mail-store-actions';
import createItem from 'owa-mail-store/lib/services/createItem';
import createResponseFromModernGroup from 'owa-mail-store/lib/services/createResponseFromModernGroupItem';
import store from 'owa-mail-store/lib/store/Store';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import Item from 'owa-service/lib/contract/Item';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import ReplyAllToItem from 'owa-service/lib/contract/ReplyAllToItem';
import ReplyToItem from 'owa-service/lib/contract/ReplyToItem';
import bodyContentType from 'owa-service/lib/factory/bodyContentType';
import replyAllToItem from 'owa-service/lib/factory/replyAllToItem';
import replyToItem from 'owa-service/lib/factory/replyToItem';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { select } from 'satcheljs/lib/legacy';

export interface CreateReplyState {
    items: ObservableMap<string, Item>;
}

// create a draft reply in a single call
export default select<CreateReplyState>({
    items: () => store.items,
})(async function createReply(
    referenceItemId: string,
    isReplyAll: boolean,
    groupId: string,
    suppressServerMarkReadOnReplyOrForward: boolean,
    state?: CreateReplyState
): Promise<Message> {
    if (!state.items.has(referenceItemId)) {
        return null;
    }
    const referenceItem = state.items.get(referenceItemId) as Message;
    const replyItem = await createReplyItem(referenceItem, isReplyAll);
    const message = await createReplyFromReplyItem(
        replyItem,
        groupId,
        suppressServerMarkReadOnReplyOrForward
    );
    return message;
});

// alternatively create an instance of a reply item to be used later to
// create the reply - this allows the caller to do something after the
// reply item has been created (e.g. so the caller can use the properties
// of the reply item such as the recipients) but before the call to
// createItem has been made
export async function createReplyItem(
    referenceItem: Message,
    isReplyAll: boolean
): Promise<ReplyToItem | ReplyAllToItem> {
    const [toRecipients, ccRecipients] = await getToCcRecipientsForReply(referenceItem, isReplyAll);
    // The BCC recipients should be carried over on a reply all to your own item (VSO 39177)
    // This property will exist on items in your Sent folder
    const bccRecipients =
        isReplyAll && referenceItem.BccRecipients ? referenceItem.BccRecipients : [];
    const bodyType = getBodyTypeForResponse(referenceItem);
    const body = bodyContentType({
        BodyType: bodyType,
        Value: buildReplyForwardMessageBody('', bodyType),
    });
    const itemId = createItemIdForRequest(referenceItem.ItemId);
    const documentId = getDocumentId(referenceItem);
    const composeOperation = isReplyAll ? ComposeOperation.ReplyAll : ComposeOperation.Reply;
    const subject = getRespondSubject(referenceItem, composeOperation);
    const item = isReplyAll
        ? replyAllToItem({
              ReferenceItemId: itemId,
              ReferenceItemDocumentId: documentId,
              ToRecipients: toRecipients,
              CcRecipients: ccRecipients,
              BccRecipients: bccRecipients,
              NewBodyContent: body,
              Subject: subject,
          })
        : replyToItem({
              ReferenceItemId: itemId,
              ReferenceItemDocumentId: documentId,
              ToRecipients: toRecipients,
              NewBodyContent: body,
              Subject: subject,
          });
    return item;
}

export async function getToCcRecipientsForReply(
    referenceItem: Message,
    isReplyAll: boolean
): Promise<[EmailAddressWrapper[], EmailAddressWrapper[]]> {
    if (isReplyAll && hasMoreRecipientsOnServer(referenceItem)) {
        await loadAllRecipientsForItem(referenceItem);
    }

    return getToCcRecipients(
        referenceItem.From
            ? referenceItem.From.Mailbox
            : referenceItem.Sender
            ? referenceItem.Sender.Mailbox
            : null,
        referenceItem.ToRecipients,
        referenceItem.CcRecipients,
        referenceItem.ReplyTo,
        isReplyAll
    );
}

// create the draft reply using a reply item returned from createReplyItem()
// - returns a promise returning either the reply message or null
// - note doesn't throw if createItems throws, just returns null
export async function createReplyFromReplyItem(
    replyItem: ReplyToItem | ReplyAllToItem,
    groupId: string,
    suppressServerMarkReadOnReplyOrForward: boolean
): Promise<Message> {
    const timeFormat = getUserConfiguration().UserOptions.TimeFormat;
    const selectedNode = getSelectedNode();
    // To fix the XRF access issue, we set RemoteExecute for archive and shared folder scenarios.
    // For CreateItem, we do not set X-AnchorMailbox header, the request will always hit primary mailbox user's mailbox server.
    // On server side code, we have code fix to check the flag when reply/replyall/forward emails.
    const remoteExecute = !!(
        selectedNode &&
        (selectedNode.treeType === SHARED_FOLDERS_TREE_TYPE ||
            selectedNode.treeType === ARCHIVE_FOLDERS_TREE_TYPE)
    );
    try {
        const createReplyResponse = groupId
            ? await createResponseFromModernGroup(
                  groupId,
                  replyItem,
                  replyItem.NewBodyContent.BodyType,
                  false /*isSend*/,
                  timeFormat
              )
            : await createItem(
                  replyItem,
                  replyItem.NewBodyContent.BodyType,
                  false /*isSend*/,
                  timeFormat,
                  false /* isInlineCompose */,
                  null /* IRMData */,
                  null /*folderId*/,
                  remoteExecute,
                  suppressServerMarkReadOnReplyOrForward
              );
        const responseMessage = createReplyResponse.ResponseMessages
            .Items[0] as ItemInfoResponseMessage;
        return responseMessage.ResponseClass === 'Success' ? responseMessage.Items[0] : null;
    } catch (error) {
        return null;
    }
}

export function hasMoreRecipientsOnServer(referenceItem: Message): boolean {
    return (
        referenceItem.RecipientCounts &&
        ((referenceItem.ToRecipients &&
            referenceItem.ToRecipients.length < referenceItem.RecipientCounts.ToRecipientsCount) ||
            (referenceItem.CcRecipients &&
                referenceItem.CcRecipients.length <
                    referenceItem.RecipientCounts.CcRecipientsCount))
    );
}
